import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { db } from '../api/firebase';
import {
  Typography,
  Paper,
  Box,
  Container,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  Business as DepartmentIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const AddProfessorPage = () => {
  const { t, isArabic } = useLanguage();
  const { user, loading: authLoading, isAdmin, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nameArabic: '',
    universityId: '',
    departmentId: '',
    agreeToTerms: false,
  });
  const [universities, setUniversities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // University name translations
  const getUniversityName = (englishName) => {
    if (!isArabic) return englishName;
    
    const translations = {
      'King Fahd University of Petroleum & Minerals': 'جامعة الملك فهد للبترول و المعادن',
      'King Saud University': 'جامعة الملك سعود',
      'Prince Sultan University': 'جامعة الأمير سلطان'
    };
    
    return translations[englishName] || englishName;
  };

  // Department name translations
  const getDepartmentName = (englishName) => {
    if (!isArabic) return englishName;
    
    const translations = {
      // KFUPM Departments
      'Aerospace Engineering': 'هندسة الطيران والفضاء',
      'Architectural Engineering': 'الهندسة المعمارية',
      'Chemical Engineering': 'الهندسة الكيميائية',
      'Civil Engineering': 'الهندسة المدنية',
      'Computer Engineering': 'هندسة الحاسب',
      'Construction Engineering and Management': 'هندسة وإدارة التشييد',
      'Electrical Engineering': 'الهندسة الكهربائية',
      'Mechanical Engineering': 'الهندسة الميكانيكية',
      'Petroleum Engineering': 'هندسة البترول',
      'Systems Engineering': 'هندسة النظم',
      'Chemistry': 'الكيمياء',
      'Earth Sciences': 'علوم الأرض',
      'Mathematical Sciences': 'العلوم الرياضية',
      'Physics': 'الفيزياء',
      'Information and Computer Science': 'علوم الحاسب والمعلومات',
      'Architecture': 'العمارة',
      'Finance & Economics': 'المالية والاقتصاد',
      'Management and Marketing': 'الإدارة والتسويق',
      
      // KSU Departments
      'Applied Studies And Community Service': 'الدراسات التطبيقية وخدمة المجتمع',
      'Business Administration': 'إدارة الأعمال',
      
      // PSU Departments
      'Accounting': 'المحاسبة',
      'Finance': 'المالية',
      'Management': 'الإدارة',
      'Marketing': 'التسويق',
      'Computer Science': 'علوم الحاسب',
      'Information Systems': 'نظم المعلومات',
      'Software Engineering': 'هندسة البرمجيات',
      'Communications and Networks Engineering': 'هندسة الاتصالات والشبكات',
      'Construction Engineering': 'هندسة الإنشاءات',
      'Production and Manufacture Engineering': 'هندسة الإنتاج والتصنيع',
      'General Studies': 'الدراسات العامة',
      'Linguistics and Translation': 'اللغويات والترجمة',
      'Mathematics and Sciences': 'الرياضيات والعلوم',
      'Law': 'القانون',
    };
    
    return translations[englishName] || englishName;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch Universities
        const universitiesCollectionRef = collection(db, 'universities');
        const universitiesSnapshot = await getDocs(universitiesCollectionRef);
        const universitiesList = universitiesSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setUniversities(universitiesList);

        // Fetch All Departments
        const departmentsCollectionRef = collection(db, 'departments');
        const departmentsSnapshot = await getDocs(departmentsCollectionRef);
        const departmentsList = departmentsSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setDepartments(departmentsList);

      } catch (err) {
        console.error("Error fetching data: ", err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter departments based on selected university
  useEffect(() => {
    if (formData.universityId) {
      const filtered = departments.filter(dept => dept.universityId === formData.universityId);
      setFilteredDepartments(filtered);
      // Reset department selection if it's not valid for the new university
      if (formData.departmentId && !filtered.find(dept => dept.id === formData.departmentId)) {
        setFormData(prev => ({ ...prev, departmentId: '' }));
      }
    } else {
      setFilteredDepartments([]);
      setFormData(prev => ({ ...prev, departmentId: '' }));
    }
  }, [formData.universityId, formData.departmentId, departments]);

  const handleInputChange = (field) => (event) => {
    const value = field === 'agreeToTerms' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Check authentication first
    if (!user) {
      setError(t('You must be logged in to add a lecturer.', 'يجب أن تكون مسجل الدخول لإضافة محاضر.'));
      return;
    }
    
    // Validation
    if (!formData.firstName.trim()) {
      setError(t('Please enter the lecturer\'s first name.', 'يرجى إدخال الاسم الأول للمحاضر.'));
      return;
    }
    if (!formData.lastName.trim()) {
      setError(t('Please enter the lecturer\'s last name.', 'يرجى إدخال الاسم الأخير للمحاضر.'));
      return;
    }
    if (!formData.universityId) {
      setError(t('Please select a university.', 'يرجى اختيار جامعة.'));
      return;
    }
    if (!formData.departmentId) {
      setError(t('Please select a department.', 'يرجى اختيار قسم.'));
      return;
    }
    if (!formData.agreeToTerms) {
      setError(t('Please agree to the terms and conditions.', 'يرجى الموافقة على الشروط والأحكام.'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Create full lecturer name
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
      
      // Check if lecturer already exists (case-insensitive)
      const existingProfQuery = query(
        collection(db, 'professors'),
        where('departmentId', '==', formData.departmentId)
      );
      const existingProfs = await getDocs(existingProfQuery);
      
      const duplicateFound = existingProfs.docs.some(doc => {
        const existingName = doc.data().name || doc.data().full_name || '';
        return existingName.toLowerCase() === fullName.toLowerCase();
      });
      
      if (duplicateFound) {
        setError(t(
          'A lecturer with this name already exists in the selected department.',
          'يوجد محاضر بهذا الاسم في القسم المحدد.'
        ));
        setSubmitting(false);
        return;
      }

      // Generate unique professor ID
      const professorCount = existingProfs.docs.length;
      const newProfessorId = `prof-${Date.now()}-${professorCount + 1}`;

      // Create lecturer data object
      const lecturerData = {
        id: newProfessorId,
        name: fullName,
        full_name: fullName,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        departmentId: formData.departmentId,
        universityId: formData.universityId,
        addedBy: user.uid,
        addedByEmail: user.email,
        addedAt: new Date().toISOString(),
        status: 'pending', // For moderation if needed
        verified: false
      };

      // Add Arabic name if provided
      if (formData.nameArabic.trim()) {
        lecturerData.nameArabic = formData.nameArabic.trim();
        // Generate search keywords from Arabic name for better search
        lecturerData.searchKeywords = [
          formData.nameArabic.trim(),
          ...formData.nameArabic.trim().split(' ').filter(word => word.length > 1)
        ];
      }

      // Add lecturer to Firestore
      const docRef = await addDoc(collection(db, 'professors'), lecturerData);
      
      setSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        nameArabic: '',
        universityId: '',
        departmentId: '',
        agreeToTerms: false,
      });
      
      // Hide success message after 5 seconds and redirect
      setTimeout(() => {
        setSuccess(false);
        navigate(`/lecturer/${docRef.id}`);
      }, 3000);
      
    } catch (err) {
      console.error("Error adding lecturer: ", err);
      setError(t(
        'Failed to add lecturer. Please try again later.',
        'فشل في إضافة المحاضر. يرجى المحاولة مرة أخرى لاحقاً.'
      ));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      setError(error.message);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show login requirement if user is not authenticated
  if (!user) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 2, direction: isArabic ? 'rtl' : 'ltr' }}>
          <Card sx={{ textAlign: 'center', py: 4 }}>
            <CardContent>
              <LoginIcon sx={{ fontSize: '4rem', color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="primary">
                {t('Login Required', 'تسجيل الدخول مطلوب')}
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                {t(
                  'You need to be logged in to add a lecturer to Me\'ar. This helps us maintain quality and prevent spam.',
                  'تحتاج إلى تسجيل الدخول لإضافة محاضر إلى معيار. هذا يساعدنا في الحفاظ على الجودة ومنع الرسائل غير المرغوب فيها.'
                )}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGoogleSignIn}
                startIcon={<LoginIcon />}
                sx={{ 
                  minWidth: 200,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  mb: 2
                }}
              >
                {t('Sign in with Google', 'تسجيل الدخول بجوجل')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'We use Google authentication to verify users and prevent abuse.',
                  'نستخدم مصادقة جوجل للتحقق من المستخدمين ومنع إساءة الاستخدام.'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Paper>
      </Container>
    );
  }

  // Show admin requirement if user is logged in but not an admin
  if (user && !isAdmin) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 2, direction: isArabic ? 'rtl' : 'ltr' }}>
          <Card sx={{ textAlign: 'center', py: 4 }}>
            <CardContent>
              <PersonAddIcon sx={{ fontSize: '4rem', color: 'error.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="error">
                {t('Admin Access Required', 'يتطلب وصول المسؤول')}
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                {t(
                  'Only administrators can add lecturers to Me\'ar. If you cannot find a lecturer, please contact support.',
                  'يمكن للمسؤولين فقط إضافة الأساتذة إلى معيار. إذا لم تتمكن من العثور على محاضر، يرجى الاتصال بالدعم.'
                )}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{ 
                  minWidth: 200,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                {t('Return Home', 'العودة للصفحة الرئيسية')}
              </Button>
            </CardContent>
          </Card>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 2, direction: isArabic ? 'rtl' : 'ltr' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <PersonAddIcon color="primary" sx={{ fontSize: '2rem' }} />
          <Typography variant="h4" color="primary" sx={{ textAlign: isArabic ? 'right' : 'left' }}>
            {t('Add a Lecturer', 'إضافة محاضر')}
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ textAlign: isArabic ? 'right' : 'left' }}>
          {t(
            'Can\'t find your lecturer? Fill the following form in order to add the lecturer to Me\'ar.',
            'لا تستطيع العثور على محاضرك؟ املأ النموذج التالي لإضافة المحاضر إلى معيار.'
          )}
        </Typography>

        <Divider sx={{ my: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t(
              'Lecturer has been successfully added to Me\'ar! Redirecting to lecturer page...',
              'تم إضافة المحاضر بنجاح إلى معيار! جاري التوجيه إلى صفحة المحاضر...'
            )}
          </Alert>
        )}

        {/* User Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('Logged in as:', 'مسجل الدخول باسم:')} <strong>{user.displayName || user.email}</strong>
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {/* First Name */}
          <TextField
            fullWidth
            label={t('Lecturer\'s First Name', 'الاسم الأول للمحاضر')}
            value={formData.firstName}
            onChange={handleInputChange('firstName')}
            margin="normal"
            required
            placeholder={t('Enter first name', 'أدخل الاسم الأول')}
            sx={{ mb: 2 }}
          />

          {/* Last Name */}
          <TextField
            fullWidth
            label={t('Lecturer\'s Last Name', 'الاسم الأخير للمحاضر')}
            value={formData.lastName}
            onChange={handleInputChange('lastName')}
            margin="normal"
            required
            placeholder={t('Enter last name', 'أدخل الاسم الأخير')}
            sx={{ mb: 2 }}
          />

          {/* Arabic Name (Optional) */}
          <TextField
            fullWidth
            label={t('Arabic Name (Optional)', 'الاسم بالعربي (اختياري)')}
            value={formData.nameArabic}
            onChange={handleInputChange('nameArabic')}
            margin="normal"
            placeholder={t('e.g. محمد أحمد الخالدي', 'مثال: محمد أحمد الخالدي')}
            helperText={t('Adding the Arabic name helps students find the lecturer easier', 'إضافة الاسم بالعربي يساعد الطلاب في العثور على المحاضر بسهولة')}
            sx={{ mb: 2 }}
            dir="rtl"
          />

          {/* University Selection */}
          <FormControl fullWidth margin="normal" required sx={{ mb: 2 }}>
            <InputLabel>{t('University', 'الجامعة')}</InputLabel>
            <Select
              value={formData.universityId}
              onChange={handleInputChange('universityId')}
              label={t('University', 'الجامعة')}
              startAdornment={<SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              {universities.map((university) => (
                <MenuItem key={university.id} value={university.id}>
                  {getUniversityName(university.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Department Selection */}
          <FormControl fullWidth margin="normal" required sx={{ mb: 3 }} disabled={!formData.universityId}>
            <InputLabel>{t('Department', 'القسم')}</InputLabel>
            <Select
              value={formData.departmentId}
              onChange={handleInputChange('departmentId')}
              label={t('Department', 'القسم')}
              startAdornment={<DepartmentIcon sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              {filteredDepartments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {getDepartmentName(department.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Terms and Conditions */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreeToTerms}
                onChange={handleInputChange('agreeToTerms')}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('I agree to the ', 'أوافق على ')}
                <Link 
                  to="/about" 
                  style={{ 
                    color: 'inherit', 
                    textDecoration: 'underline',
                    fontWeight: 'bold'
                  }}
                >
                  {t('Terms and Conditions & Privacy Policy', 'الشروط والأحكام وسياسة الخصوصية')}
                </Link>
              </Typography>
            }
            sx={{ 
              mb: 3,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                textAlign: isArabic ? 'right' : 'left'
              }
            }}
          />

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: isArabic ? 'flex-end' : 'flex-start' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <PersonAddIcon />}
              sx={{ 
                minWidth: 200,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem'
              }}
            >
              {submitting 
                ? t('Submitting...', 'جاري الإرسال...') 
                : t('Add Lecturer', 'إضافة محاضر')
              }
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddProfessorPage; 